import type { NotificationView } from '../types'
import type { CopyRenderer, CopySegment } from './types'

/**
 * The secondary line under the sentence. Same rule as the share copy: a lesson name only
 * makes sense next to the course it belongs to.
 *
 * The lesson name is dropped as soon as the card covers more than one subject. A roll-up
 * freezes its context on the first occurrence (`emitRollup` never rewrites it, so an
 * already-seen card cannot change its subject), which would otherwise leave a card reading
 * "sudah menilai 4 hasil kerja" still naming the single lesson it started from.
 */
function contextLineOf(view: NotificationView): string | undefined {
  const { courseName, lessonName } = view.context
  if (lessonName && courseName && view.subjectCount <= 1) return `${lessonName} dalam tugas ${courseName}`
  return courseName ?? lessonName
}

/** Roll-up totals are only trustworthy from 1 up; a 0 from a bad producer reads as 1. */
function subjectsIn(view: NotificationView): number {
  return Math.max(1, view.subjectCount)
}

// --- Student side -----------------------------------------------------------------

/** "{Teacher} memberikan tugas baru buat kamu." */
export const renderAssignmentAssignedCopy: CopyRenderer = (view) => ({
  body: [
    { text: view.snapshot.actorName ?? 'Gurumu', bold: true },
    { text: ' memberikan ' },
    { text: 'tugas baru', bold: true },
    { text: ' buat kamu.' },
  ],
  contextLine: contextLineOf(view),
})

/**
 * "{Teacher} sudah menilai hasil kerja kamu. Lihat hasilnya!"
 *
 * Grouped: a teacher marking six lessons of one course in a sitting is one card counting
 * up, not six cards and a badge reading six.
 */
export const renderAssignmentGradedCopy: CopyRenderer = (view) => {
  const graded = subjectsIn(view)

  const body: CopySegment[] = [
    { text: view.snapshot.actorName ?? 'Gurumu', bold: true },
    { text: ' ' },
    graded === 1
      ? { text: 'sudah menilai hasil kerja', bold: true }
      : { text: `sudah menilai ${graded} hasil kerja`, bold: true },
    { text: ' kamu. Lihat hasilnya!' },
  ]

  return { body, contextLine: contextLineOf(view) }
}

/** "Waktu pengerjaan tugas ini akan segera berakhir. Ayo segera selesaikan!" */
export const renderAssignmentDueSoonCopy: CopyRenderer = (view) => ({
  body: [
    { text: 'Waktu pengerjaan ' },
    { text: 'tugas ini akan segera berakhir.', bold: true },
    { text: ' Ayo segera selesaikan!' },
  ],
  contextLine: contextLineOf(view),
})

/** "Wow! Ada tantangan baru khusus buat kamu. Ayo mulai kerjakan!" */
export const renderChallengeAssignedCopy: CopyRenderer = (view) => ({
  body: [
    { text: 'Wow! Ada ' },
    { text: 'tantangan baru', bold: true },
    { text: ' khusus buat kamu. Ayo mulai kerjakan!' },
  ],
  contextLine: contextLineOf(view),
})

/** "Waktu pengerjaan tantangan ini akan segera berakhir. Ayo segera selesaikan!" */
export const renderChallengeDueSoonCopy: CopyRenderer = (view) => ({
  body: [
    { text: 'Waktu pengerjaan ' },
    { text: 'tantangan ini akan segera berakhir.', bold: true },
    { text: ' Ayo segera selesaikan!' },
  ],
  contextLine: contextLineOf(view),
})

// --- Teacher side -----------------------------------------------------------------

/**
 * "{n} hasil kerja siswa menunggu dinilai."
 *
 * The card this whole roll-up mechanism exists for. Thirty students submitting inside a
 * minute produce one card whose number climbs, never thirty cards.
 */
export const renderAssignmentNeedsGradingCopy: CopyRenderer = (view) => ({
  body: [
    { text: `${subjectsIn(view)} hasil kerja siswa `, bold: false },
    { text: 'menunggu dinilai', bold: true },
    { text: '.' },
  ],
  contextLine: contextLineOf(view),
})

/** "{n} siswa belum memulai tugas yang diberikan." */
export const renderAssignmentNotStartedCopy: CopyRenderer = (view) => ({
  body: [
    { text: `${subjectsIn(view)} siswa ` },
    { text: 'belum memulai tugas', bold: true },
    { text: ' yang diberikan.' },
  ],
  contextLine: contextLineOf(view),
})

/** "{n} siswa belum menyelesaikan tugas (berakhir besok)." */
export const renderAssignmentIncompleteCopy: CopyRenderer = (view) => ({
  body: [
    { text: `${subjectsIn(view)} siswa ` },
    { text: 'belum menyelesaikan tugas', bold: true },
    { text: ' (berakhir besok).' },
  ],
  contextLine: contextLineOf(view),
})

/** "Semua siswa telah berhasil menyelesaikan tugas." */
export const renderAssignmentAllCompletedCopy: CopyRenderer = (view) => ({
  body: [{ text: 'Semua siswa telah berhasil ' }, { text: 'menyelesaikan tugas', bold: true }, { text: '.' }],
  contextLine: contextLineOf(view),
})

// --- Tugas dan Tantangan -----------------------------------------------------------

/**
 * Tugas dan Tantangan copy.
 *
 * One audience only. A teacher with no rombel presses "Hubungi admin" on the blocker, the school's
 * owners and admins get the card, the teacher gets a toast. There is deliberately no ACTOR renderer
 * — the event is a roll-up shared by every teacher waiting in the same school, so an actor row on
 * it would show teacher A a card about teacher B.
 */

const UNKNOWN_TEACHER = 'Seorang guru'

/**
 * "{Guru} menunggu kelas untuk diampu. Tugaskan ke kelas sekarang agar dia dapat mulai mengajar."
 * "{Guru} dan {n} lainnya menunggu kelas untuk diampu. Tugaskan ke kelas sekarang agar mereka …"
 *
 * Counts `subjectCount` — the teachers folded into this group by `emitRollup` — not
 * `recipientCount`, which is how many admins were told. `actorName` is frozen at the first request
 * in the bucket, so the sentence keeps naming whoever asked first however large the group grows;
 * that is also the avatar `RecipientNotificationCard` draws, so the two always agree.
 *
 * Both sentences live in `body`: the emphasis in the design falls mid-sentence on the instruction,
 * which `contextLine` (a secondary line, label-sm) cannot express.
 */
export const renderAssignmentClassRequestedRecipientCopy: CopyRenderer = (view) => {
  const teacherName = view.snapshot.actorName ?? UNKNOWN_TEACHER
  // `subjectCount` is never 0 on anything this producer writes, but a hand-seeded or pre-roll-up
  // row would otherwise render "dan -1 lainnya".
  const others = Math.max(0, view.subjectCount - 1)

  const body: CopySegment[] = [{ text: teacherName, bold: true }]

  if (others > 0) {
    body.push({ text: ' dan ' }, { text: `${others} lainnya`, bold: true })
  }

  body.push(
    { text: ' menunggu kelas untuk diampu. ' },
    { text: 'Tugaskan ke kelas', bold: true },
    { text: ` sekarang agar ${others > 0 ? 'mereka' : 'dia'} dapat mulai mengajar.` },
  )

  return { body }
}
